import React, { useState, useEffect } from 'react';
import './OvertimeAnalytics.css';

interface OvertimeData {
  employeeId: string;
  employeeName: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalDoubleTimeHours: number;
  entriesProcessed: number;
  byLocation: Record<string, {
    regularMinutes: number;
    overtimeMinutes: number;
    doubleTimeMinutes: number;
    state?: string;
    city?: string;
  }>;
  byDate: Record<string, {
    regularMinutes: number;
    overtimeMinutes: number;
    doubleTimeMinutes: number;
    locations: string[];
  }>;
  crossLocationOvertime: boolean;
  violations: Array<{
    timeEntryId: string;
    error: string;
  }>;
}

interface PayPeriodSummary {
  payPeriodStart: string;
  payPeriodEnd: string;
  employeesProcessed: number;
  results: OvertimeData[];
  summary: {
    totalRegularHours: number;
    totalOvertimeHours: number;
    totalDoubleTimeHours: number;
    employeesWithOvertime: number;
    employeesWithDoubleTime: number;
    employeesWithCrossLocationOvertime: number;
    violations: number;
  };
}

interface OvertimeAnalyticsProps {
  storeId?: string;
  enterpriseId: string;
}

export const OvertimeAnalytics: React.FC<OvertimeAnalyticsProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payPeriodData, setPayPeriodData] = useState<PayPeriodSummary | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<OvertimeData | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [reportType, setReportType] = useState<'summary' | 'detailed' | 'violations'>('summary');
  const [sortBy, setSortBy] = useState<'name' | 'overtime' | 'violations'>('overtime');
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');

  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 14); // Last 2 weeks
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      generateReport();
    }
  }, [dateRange, storeId, enterpriseId]);

  const generateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      if (storeId) params.append('storeId', storeId);

      const response = await fetch(`/api/time-entries/overtime/calculate?${params}`);
      if (!response.ok) throw new Error('Failed to generate overtime report');

      const data = await response.json();
      setPayPeriodData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getSortedEmployees = () => {
    if (!payPeriodData?.results) return [];

    const sorted = [...payPeriodData.results];
    
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
      case 'overtime':
        return sorted.sort((a, b) => b.totalOvertimeHours - a.totalOvertimeHours);
      case 'violations':
        return sorted.sort((a, b) => b.violations.length - a.violations.length);
      default:
        return sorted;
    }
  };

  const calculateTotalCost = (employee: OvertimeData) => {
    // This would need to integrate with payroll data for actual rates
    const estimatedRegularRate = 15; // Base rate estimate
    const overtimeCost = employee.totalOvertimeHours * estimatedRegularRate * 1.5;
    const doubleTimeCost = employee.totalDoubleTimeHours * estimatedRegularRate * 2.0;
    return overtimeCost + doubleTimeCost;
  };

  const formatHours = (minutes: number) => {
    return (minutes / 60).toFixed(2);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const exportData = async () => {
    if (!payPeriodData) return;

    if (exportFormat === 'csv') {
      const csvData = generateCSV();
      downloadCSV(csvData, `overtime-report-${dateRange.startDate}-${dateRange.endDate}.csv`);
    } else {
      // PDF export would require additional library like jsPDF
      alert('PDF export feature coming soon!');
    }
  };

  const generateCSV = () => {
    if (!payPeriodData) return '';

    const headers = [
      'Employee Name',
      'Regular Hours',
      'Overtime Hours',
      'Double Time Hours',
      'Cross-Location OT',
      'Violations',
      'Estimated OT Cost'
    ];

    const rows = payPeriodData.results.map(employee => [
      employee.employeeName,
      employee.totalRegularHours.toFixed(2),
      employee.totalOvertimeHours.toFixed(2),
      employee.totalDoubleTimeHours.toFixed(2),
      employee.crossLocationOvertime ? 'Yes' : 'No',
      employee.violations.length.toString(),
      calculateTotalCost(employee).toFixed(2)
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getViolationSeverity = (violationCount: number) => {
    if (violationCount === 0) return 'none';
    if (violationCount <= 2) return 'low';
    if (violationCount <= 5) return 'medium';
    return 'high';
  };

  if (loading) return <div className="loading">Generating overtime report...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="overtime-analytics">
      <div className="analytics-header">
        <h2>Overtime Analytics & Reporting</h2>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={generateReport}
            disabled={loading}
          >
            🔄 Refresh
          </button>
          <button 
            className="btn btn-primary"
            onClick={exportData}
            disabled={!payPeriodData}
          >
            📊 Export
          </button>
        </div>
      </div>

      <div className="report-controls">
        <div className="date-controls">
          <div className="form-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              className="form-input"
            />
          </div>
        </div>

        <div className="filter-controls">
          <div className="form-group">
            <label>Report Type:</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as any)}
              className="form-input"
            >
              <option value="summary">Summary</option>
              <option value="detailed">Detailed</option>
              <option value="violations">Violations Only</option>
            </select>
          </div>

          <div className="form-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="form-input"
            >
              <option value="overtime">Overtime Hours</option>
              <option value="name">Employee Name</option>
              <option value="violations">Violations</option>
            </select>
          </div>

          <div className="form-group">
            <label>Export Format:</label>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as any)}
              className="form-input"
            >
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
        </div>
      </div>

      {payPeriodData && (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-header">
                <h4>Total Employees</h4>
                <span className="card-value">{payPeriodData.employeesProcessed}</span>
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Total Overtime</h4>
                <span className="card-value">{payPeriodData.summary.totalOvertimeHours.toFixed(1)}h</span>
              </div>
              <div className="card-subtitle">
                {payPeriodData.summary.employeesWithOvertime} employees
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Double Time</h4>
                <span className="card-value">{payPeriodData.summary.totalDoubleTimeHours.toFixed(1)}h</span>
              </div>
              <div className="card-subtitle">
                {payPeriodData.summary.employeesWithDoubleTime} employees
              </div>
            </div>

            <div className="summary-card">
              <div className="card-header">
                <h4>Cross-Location OT</h4>
                <span className="card-value">{payPeriodData.summary.employeesWithCrossLocationOvertime}</span>
              </div>
              <div className="card-subtitle">employees affected</div>
            </div>

            <div className="summary-card warning">
              <div className="card-header">
                <h4>Violations</h4>
                <span className="card-value">{payPeriodData.summary.violations}</span>
              </div>
              <div className="card-subtitle">require attention</div>
            </div>
          </div>

          <div className="analytics-content">
            {reportType === 'summary' && (
              <div className="employee-summary-table">
                <div className="table-header">
                  <div className="header-cell">Employee</div>
                  <div className="header-cell">Regular Hours</div>
                  <div className="header-cell">Overtime Hours</div>
                  <div className="header-cell">Double Time</div>
                  <div className="header-cell">Est. OT Cost</div>
                  <div className="header-cell">Status</div>
                  <div className="header-cell">Actions</div>
                </div>

                {getSortedEmployees().map((employee) => (
                  <div key={employee.employeeId} className="table-row">
                    <div className="cell employee-info">
                      <div className="employee-name">{employee.employeeName}</div>
                      <div className="employee-details">
                        {employee.entriesProcessed} time entries
                      </div>
                    </div>

                    <div className="cell">
                      <span className="hours-value">{employee.totalRegularHours.toFixed(1)}h</span>
                    </div>

                    <div className="cell">
                      <span className={`hours-value ${employee.totalOvertimeHours > 0 ? 'overtime' : ''}`}>
                        {employee.totalOvertimeHours.toFixed(1)}h
                      </span>
                    </div>

                    <div className="cell">
                      <span className={`hours-value ${employee.totalDoubleTimeHours > 0 ? 'double-time' : ''}`}>
                        {employee.totalDoubleTimeHours.toFixed(1)}h
                      </span>
                    </div>

                    <div className="cell">
                      <span className="cost-value">
                        {formatCurrency(calculateTotalCost(employee))}
                      </span>
                    </div>

                    <div className="cell">
                      <div className="status-indicators">
                        {employee.crossLocationOvertime && (
                          <span className="indicator cross-location" title="Cross-location overtime">
                            🏢
                          </span>
                        )}
                        {employee.violations.length > 0 && (
                          <span 
                            className={`indicator violation ${getViolationSeverity(employee.violations.length)}`}
                            title={`${employee.violations.length} violations`}
                          >
                            ⚠️
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="cell actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => setSelectedEmployee(employee)}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {reportType === 'violations' && (
              <div className="violations-view">
                <h3>Overtime Violations</h3>
                {payPeriodData.results
                  .filter(emp => emp.violations.length > 0)
                  .map((employee) => (
                    <div key={employee.employeeId} className="violation-card">
                      <div className="violation-header">
                        <h4>{employee.employeeName}</h4>
                        <span className={`violation-count ${getViolationSeverity(employee.violations.length)}`}>
                          {employee.violations.length} violation(s)
                        </span>
                      </div>
                      <div className="violation-list">
                        {employee.violations.map((violation, index) => (
                          <div key={index} className="violation-item">
                            <span className="violation-error">{violation.error}</span>
                            <span className="violation-id">Entry: {violation.timeEntryId}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                
                {payPeriodData.results.filter(emp => emp.violations.length > 0).length === 0 && (
                  <div className="no-violations">
                    <p>✅ No overtime violations found for this period!</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>{selectedEmployee.employeeName} - Overtime Details</h3>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedEmployee(null)}
              >
                Close
              </button>
            </div>
            
            <div className="modal-content">
              <div className="employee-overtime-details">
                <div className="detail-section">
                  <h4>Summary</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Regular Hours:</label>
                      <span>{selectedEmployee.totalRegularHours.toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Overtime Hours:</label>
                      <span className="overtime">{selectedEmployee.totalOvertimeHours.toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Double Time Hours:</label>
                      <span className="double-time">{selectedEmployee.totalDoubleTimeHours.toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Estimated Cost:</label>
                      <span>{formatCurrency(calculateTotalCost(selectedEmployee))}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>By Location</h4>
                  <div className="location-breakdown">
                    {Object.entries(selectedEmployee.byLocation).map(([storeId, data]) => (
                      <div key={storeId} className="location-item">
                        <div className="location-name">Store {storeId}</div>
                        {data.state && <div className="location-state">{data.city}, {data.state}</div>}
                        <div className="location-hours">
                          <span>Regular: {formatHours(data.regularMinutes)}h</span>
                          <span>OT: {formatHours(data.overtimeMinutes)}h</span>
                          <span>DT: {formatHours(data.doubleTimeMinutes)}h</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Daily Breakdown</h4>
                  <div className="daily-breakdown">
                    {Object.entries(selectedEmployee.byDate)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([date, data]) => (
                        <div key={date} className="daily-item">
                          <div className="daily-date">{new Date(date).toLocaleDateString()}</div>
                          <div className="daily-hours">
                            <span>Regular: {formatHours(data.regularMinutes)}h</span>
                            <span>OT: {formatHours(data.overtimeMinutes)}h</span>
                            <span>DT: {formatHours(data.doubleTimeMinutes)}h</span>
                          </div>
                          <div className="daily-locations">
                            {data.locations.length} location(s)
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {selectedEmployee.violations.length > 0 && (
                  <div className="detail-section">
                    <h4>Violations</h4>
                    <div className="violation-list">
                      {selectedEmployee.violations.map((violation, index) => (
                        <div key={index} className="violation-item">
                          <span className="violation-error">{violation.error}</span>
                          <span className="violation-id">Entry: {violation.timeEntryId}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};